"use client"

import { BffApiClientMolecule } from "@/app/api-client/store"
import { BlockchainFiltersMoleculeReatom } from "@/app/blockchains/filters/blockchain-filters-molecule.reatom"
import { ErrorMessage } from "@/app/error"
import { GridLayout } from "@/app/grid"
import type { Drop } from "@anaconda/core/drop/types"
import { DEFAULT_PAGE_SIZE } from "@anaconda/core/nft/search/constants"
import { Trans } from "@lingui/react/macro"
import { wrap } from "@reatom/core"
import { reatomComponent } from "@reatom/react"
import { useQuery } from "@tanstack/react-query"
import { useMolecule } from "bunshi/react"
import { Suspense, useEffect } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useDropsScope } from "../scope-provider"
import { BatchMintedDataMoleculeReatom } from "../store/batch-minted-molecule.reatom"
import { getDropsQueryParams } from "../utils"
import { DropItemSkeleton } from "./drop-item"
import { DropsList } from "./drops-list"

type DropsResponse = Drop[]

interface Props {
  className?: string
}

function ErrorFallback() {
  return <ErrorMessage />
}

export function DropsSkeleton() {
  const placeholders = Array.from({ length: DEFAULT_PAGE_SIZE })

  return (
    <GridLayout>
      {placeholders.map((_, index) => (
        <DropItemSkeleton key={index} />
      ))}
    </GridLayout>
  )
}

const DropsContent = reatomComponent(() => {
  const { size, status } = useDropsScope()
  const { blockchainsQueryFilter } = useMolecule(
    BlockchainFiltersMoleculeReatom,
  )
  const blockchains = blockchainsQueryFilter()
  const apiClient = useMolecule(BffApiClientMolecule)
  const { fetchBatchMinted, refetchEffect } = useMolecule(
    BatchMintedDataMoleculeReatom,
  )

  const { data: drops, isLoading } = useQuery<DropsResponse>(
    getDropsQueryParams(apiClient, { size, status, blockchains }),
  )

  // Start the refetch effect (subscribes to it by referencing, not calling)
  refetchEffect

  // Fetch batch minted data when drops load
  useEffect(() => {
    if (drops && drops.length > 0) {
      const dropIds = drops.map((drop) => drop.id)
      wrap(fetchBatchMinted)(dropIds)
    }
  }, [drops, fetchBatchMinted])

  if (isLoading) {
    return <DropsSkeleton />
  }

  if (!drops?.length) {
    return <EmptyDrops />
  }

  return <DropsList drops={drops} />
}, "DropsContent")

export function DropsView(props: Props) {
  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <Suspense fallback={<DropsSkeleton />}>
        <DropsContent />
      </Suspense>
    </ErrorBoundary>
  )
}

function EmptyDrops() {
  return (
    <div className="flex flex-1 flex-col">
      <div className="flex flex-1 items-center justify-center">
        <h2 className="text-center text-2xl">
          <Trans>Nothing here yet...</Trans>
        </h2>
      </div>
    </div>
  )
}
